import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Answer, AnswersDetails } from '../models/Answer.model';
import { environment } from 'src/environment';
import { EReplyStatus } from '../models/recipients.model';
import { QueueService } from './queue-requests.service';
import { TostesStore } from '../store/tostes-store';
import { TostTypeEnum } from '../shared/models/tostes.model';
import { saveAs } from 'file-saver';
import { User } from '../models/user.model';
import { ApproverCandidate } from '../models/approverCandidate.model';

@Injectable({
  providedIn: 'root'
})
export class AnswersApiService {
  #httpservice = inject(HttpClient);
  #queueService = inject(QueueService);
  #tostesStore = inject(TostesStore);
  //#baseUrl = inject(ConfigurationService).config.serverUrl;
  #baseUrl = environment.apiUrl;
  #specBaseUrl = `${this.#baseUrl}/answers`
  deleteModularRow = (questionId: number, order: number, questionnaireGuid: string) => {
    return this.#queueService.enqueue(() =>
      this.#httpservice.post<number>(`${this.#specBaseUrl}/deleteModularRow`, { questionId, order, guid: questionnaireGuid }));
  }

  saveAnswers = (questionnaireGuid: string, answers?: Answer[], isSent?: boolean, mainAnswer?: string, approvers?: { approverId1?: number, groupId1?: number, approverId2?: number, groupId2?: number }, requiredAmount?: number) => {
    return this.#httpservice.post<number>(`${this.#specBaseUrl}/save`, { guid: questionnaireGuid, answers, isSent, mainAnswer, approvers, requiredAmount });
  }

  getAnswers = (questionnaireGuid: string) => {
    return this.#httpservice.get<AnswersDetails>(`${this.#specBaseUrl}/getAnswersByGuid/${questionnaireGuid}`)
  }

  canAnswerToQuestionnarie = (questionnaireGuid: string) => {
    return this.#httpservice.get<{ status: EReplyStatus, submissionDateNTZ: Date, isUnauthorized: boolean }>(`${this.#specBaseUrl}/canAnswerToQuestionnarie/${questionnaireGuid}`)
  }

  downloadPdfToUser(questionnaireId: number, name?: string, guidToUser?: string) {
    const guid = this.#tostesStore.generateGuid();
    this.#tostesStore.addTost({
      guid,
      message: { en: "Download PDF", he: "מוריד PDF.." },
      type: TostTypeEnum.LOAD
    });

    this.#httpservice.post(`${this.#specBaseUrl}/downloadPdfToUser`, { questionnaireId, guid: guidToUser, date: getNowDateToString() }, { responseType: 'blob' })
      .subscribe(
        (blob: Blob) => {
          const contentType = blob.type;
          let fileName = `${name?.slice(0, 100) ?? "data"}`;

          if (contentType === 'application/zip') {
            fileName += '.zip'; // If the response is a ZIP file
          } else if (contentType === 'application/pdf') {
            fileName += '.pdf'; // If the response is a PDF file
          } else {
            // If the content type is unexpected, assume it's a PDF for backward compatibility
            fileName += '.pdf';
          }
          // Use FileSaver.js to save the file
          saveAs(blob, fileName);
          this.#tostesStore.removeTostByGuid(guid);
        },
        (error) => {
          this.#tostesStore.removeTostByGuid(guid);
          this.#tostesStore.addTost({
            guid,
            message: { en: "An error occurred while downloading PDF", he: "ארעה שגיאה בהורדת PDF" },
            type: TostTypeEnum.ERROR,
            seconds: 5
          });
        }
      );
  }
  addUnauthorizedUrl(questionnaireId: number) {
    return this.#httpservice.get<string>(`${this.#specBaseUrl}/addUnauthorizedUrl/${questionnaireId}`);
  }

  getApproversAdmins(questionnaireId: number) {
    return this.#httpservice.get<ApproverCandidate[]>(`${this.#specBaseUrl}/getApproversAdmins/${questionnaireId}`);
  }

  getFileByGuid(guid: string) {
    return this.#httpservice.get(`${this.#specBaseUrl}/getFileByGuid/${guid}`, {
      responseType: 'blob',
      observe: 'response'
    });
  }
}
export const getNowDateToString = () => {
  const now = new Date();

  // Get components of the local date
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  // Format as YYYY-MM-DDTHH:mm
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}